import { useState, useEffect } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  WIDGET_DEFINITIONS,
  DEFAULT_ROLE_CONFIGS,
  type RoleWidgetConfig,
  type WidgetDefinition,
} from "@/lib/widget-registry";
import { useWidgetConfigs, useSaveWidgetConfig } from "@/hooks/use-widget-configs";
import { WIDGET_DATA_DOCS } from "@/components/dashboard/widgets/WidgetRenderer";
import { AvailableWidgetsSection } from "@/components/dashboard/widgets/AvailableWidgetsSection";
import { SortableWidgetPreviewCard } from "./SortableWidgetPreviewCard";
import { SYSTEM_ROLE_LABELS, SYSTEM_ROLE_OPTIONS } from "@/lib/constants";
import { toast } from "sonner";
import { SrIcon } from "@/components/ui/SrIcon";
import { PerUserOverridesPanel } from "./PerUserOverridesPanel";

// VIB-708-followup: kun aktive roller vises i editoren. Legacy-labels
// (employee, kunderadgiver, regnskapsforer, project_lead) holdes ute så
// admin ikke ved et uhell lagrer mot en rolle ingen brukere har.
const EDITOR_ROLES = SYSTEM_ROLE_OPTIONS.map((role) => ({
  role,
  label: SYSTEM_ROLE_LABELS[role] ?? role,
}));

/**
 * WidgetAdminEditor — admin-flyt for widget-konfigurasjon.
 *
 * Forenet visning (VIB-708-followup): hver aktive widget rendres live
 * med en topplinje for drag-reorder, size-toggle og fjern. Tilgjengelige
 * widgets vises gruppert under listen. Ingen Preview/Konfig-fane lenger.
 *
 * "Per ansatt"-fanen er separat — se PerUserOverridesPanel.
 */
export function WidgetAdminEditor() {
  const { data: dbConfigs, isLoading: isLoadingConfigs } = useWidgetConfigs();
  const saveConfig = useSaveWidgetConfig();

  const [configs, setConfigs] = useState<RoleWidgetConfig[]>(
    () => JSON.parse(JSON.stringify(DEFAULT_ROLE_CONFIGS)),
  );
  const [activeRole, setActiveRole] = useState<string>(SYSTEM_ROLE_OPTIONS[0]);
  const [selectedWidget, setSelectedWidget] = useState<string | null>(null);
  const [hasUnsaved, setHasUnsaved] = useState(false);
  const [perUserView, setPerUserView] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Sync local state when DB data loads. VIB-708-followup: vi merger DB
  // med DEFAULT_ROLE_CONFIGS slik at en aktiv rolle uten DB-rad (eller
  // med tom widgets-array) får default-widgets istedenfor blank-state.
  useEffect(() => {
    if (!dbConfigs) return;
    const dbByRole = new Map(dbConfigs.map((c) => [c.role, c]));
    const merged = SYSTEM_ROLE_OPTIONS.map((role) => {
      const fromDb = dbByRole.get(role);
      if (fromDb && fromDb.widgets.length > 0)
        return JSON.parse(JSON.stringify(fromDb));
      const fromDefault = DEFAULT_ROLE_CONFIGS.find((c) => c.role === role);
      return JSON.parse(JSON.stringify(fromDefault ?? { role, widgets: [] }));
    });
    setConfigs(merged);
    setHasUnsaved(false);
  }, [dbConfigs]);

  const activeConfig = configs.find((c) => c.role === activeRole);
  const sortedWidgets = [...(activeConfig?.widgets ?? [])].sort(
    (a, b) => a.order - b.order,
  );
  const activeKeys = new Set(sortedWidgets.map((w) => w.key));
  const available = WIDGET_DEFINITIONS.filter((w) => !activeKeys.has(w.key));

  function updateConfig(updater: (prev: RoleWidgetConfig[]) => RoleWidgetConfig[]) {
    setConfigs(updater);
    setHasUnsaved(true);
  }

  function addWidget(widget: WidgetDefinition) {
    updateConfig((prev) =>
      prev.map((c) => {
        if (c.role !== activeRole) return c;
        const maxOrder = Math.max(0, ...c.widgets.map((w) => w.order));
        return {
          ...c,
          widgets: [
            ...c.widgets,
            { key: widget.key, order: maxOrder + 1, size: widget.defaultSize },
          ],
        };
      }),
    );
  }

  function removeWidget(key: string) {
    if (selectedWidget === key) setSelectedWidget(null);
    updateConfig((prev) =>
      prev.map((c) => {
        if (c.role !== activeRole) return c;
        return {
          ...c,
          widgets: c.widgets
            .filter((w) => w.key !== key)
            .map((w, i) => ({ ...w, order: i + 1 })),
        };
      }),
    );
  }

  function cycleSize(key: string) {
    const sizes: Array<"full" | "half" | "third"> = ["full", "half", "third"];
    updateConfig((prev) =>
      prev.map((c) => {
        if (c.role !== activeRole) return c;
        return {
          ...c,
          widgets: c.widgets.map((w) => {
            if (w.key !== key) return w;
            const currentIdx = sizes.indexOf(w.size);
            return { ...w, size: sizes[(currentIdx + 1) % sizes.length] };
          }),
        };
      }),
    );
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = sortedWidgets.findIndex((w) => w.key === active.id);
    const newIndex = sortedWidgets.findIndex((w) => w.key === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(sortedWidgets, oldIndex, newIndex).map(
      (w, i) => ({ ...w, order: i + 1 }),
    );
    updateConfig((prev) =>
      prev.map((c) =>
        c.role === activeRole ? { ...c, widgets: reordered } : c,
      ),
    );
  }

  async function handleSaveAll() {
    try {
      await saveConfig.mutateAsync(configs);
      setHasUnsaved(false);
      toast.success("Alle widget-oppsett lagret");
    } catch {
      toast.error("Kunne ikke lagre widget-oppsett");
    }
  }

  const selectedDef = selectedWidget
    ? WIDGET_DEFINITIONS.find((d) => d.key === selectedWidget)
    : null;
  const selectedDocs = selectedWidget ? WIDGET_DATA_DOCS[selectedWidget] : null;

  if (isLoadingConfigs) {
    return (
      <div className="flex items-center justify-center py-20">
        <SrIcon name="loader2" className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-2 flex-wrap items-center">
          {EDITOR_ROLES.map(({ role, label }) => (
            <Button
              key={role}
              variant={!perUserView && activeRole === role ? "default" : "outline"}
              size="sm"
              onClick={() => {
                setActiveRole(role);
                setPerUserView(false);
                setSelectedWidget(null);
              }}
            >
              {label}
            </Button>
          ))}
          <div className="h-5 w-px bg-border mx-1" />
          <Button
            variant={perUserView ? "default" : "outline"}
            size="sm"
            onClick={() => setPerUserView(true)}
          >
            <SrIcon name="users" className="w-3.5 h-3.5 mr-1.5" />
            Per ansatt
          </Button>
        </div>
        <div className="flex items-center gap-2">
          {!perUserView && hasUnsaved && (
            <Badge
              variant="secondary"
              className="bg-amber-500/10 text-amber-600 text-xs"
            >
              Ulagrede endringer
            </Badge>
          )}
          {!perUserView && (
            <Button
              size="sm"
              variant="outline"
              disabled={!hasUnsaved || saveConfig.isPending}
              onClick={handleSaveAll}
            >
              {saveConfig.isPending ? (
                <SrIcon name="loader2" className="w-4 h-4 mr-1.5 animate-spin" />
              ) : (
                <SrIcon name="save" className="w-4 h-4 mr-1.5" />
              )}
              Lagre alle
            </Button>
          )}
        </div>
      </div>

      {/* Main content */}
      {perUserView ? (
        <PerUserOverridesPanel />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Widget-liste — sortable + live preview + add-section */}
          <div className="lg:col-span-3 space-y-4">
            {sortedWidgets.length === 0 ? (
              <div className="p-8 border rounded-md text-center text-sm text-muted-foreground">
                Ingen widgets for {SYSTEM_ROLE_LABELS[activeRole] ?? activeRole}.
                Velg fra listen under for å legge til.
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={sortedWidgets.map((w) => w.key)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-4">
                    {sortedWidgets.map((w) => (
                      <SortableWidgetPreviewCard
                        key={w.key}
                        widgetKey={w.key}
                        size={w.size}
                        isSelected={selectedWidget === w.key}
                        onSelect={setSelectedWidget}
                        onCycleSize={cycleSize}
                        onRemove={removeWidget}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}

            <AvailableWidgetsSection available={available} onAdd={addWidget} />
          </div>

          {/* Info panel */}
          <div className="space-y-4">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Widget-info
            </h2>
            {selectedDef && selectedDocs ? (
              <Card>
                <CardContent className="p-4 space-y-4">
                  <div>
                    <h3 className="font-semibold text-sm">{selectedDef.name}</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      {selectedDef.description}
                    </p>
                  </div>

                  <div>
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <SrIcon name="code2" className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="text-xs font-medium text-muted-foreground">
                        Hook
                      </span>
                    </div>
                    <code className="text-xs bg-muted px-2 py-1 rounded block">
                      {selectedDocs.hook}
                    </code>
                  </div>

                  <div>
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <SrIcon name="database" className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="text-xs font-medium text-muted-foreground">
                        Tabeller
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {selectedDocs.tables.map((t) => (
                        <Badge key={t} variant="outline" className="text-[10px]">
                          {t}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div>
                    <span className="text-xs font-medium text-muted-foreground block mb-1.5">
                      Beregning
                    </span>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {selectedDocs.formula}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <p className="text-sm text-muted-foreground">
                Klikk på en widget for å se datakilde og beregningslogikk.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
